import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, StatusPill } from "@/components/Shell";
import { ADMIN } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Search } from "lucide-react";

const STATUSES = [
  { v: "all", label: "All statuses" },
  { v: "submitted", label: "Submitted" },
  { v: "under_review", label: "Under review" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "on_hold", label: "On hold" },
];

export default function AdminCandidatesPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [intakeId, setIntakeId] = useState("all");
  const [project, setProject] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [intakes, setIntakes] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cat }, { data: it }] = await Promise.all([
          api.get("/catalog/choices"),
          api.get("/admin/intakes"),
        ]);
        setProjects(cat.projects);
        setIntakes(it);
      } catch (err) {
        console.error("Failed to load filter options:", err);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/candidates", {
        params: {
          status,
          q: q || undefined,
          intake_id: intakeId,
          project,
        },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, intakeId, project, q]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Section kicker="Directory" title="All candidates">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <div className="label-brut">Status</div>
          <select
            className="select-brut min-w-[160px]"
            data-testid={ADMIN.filterStatus}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUSES.map((s) => (
              <option key={s.v} value={s.v}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label-brut">Client</div>
          <select
            className="select-brut min-w-[200px]"
            data-testid={ADMIN.filterIntake}
            value={intakeId}
            onChange={(e) => setIntakeId(e.target.value)}
          >
            <option value="all">All clients</option>
            {intakes.map((it) => (
              <option key={it.id} value={it.id}>{it.client_name}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="label-brut">Project</div>
          <select
            className="select-brut min-w-[180px]"
            data-testid={ADMIN.filterProject}
            value={project}
            onChange={(e) => setProject(e.target.value)}
          >
            <option value="all">All projects</option>
            {projects.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="label-brut">Search</div>
          <div className="flex gap-2">
            <input
              className="input-brut"
              data-testid={ADMIN.searchInput}
              placeholder="Name, email, client, or reference"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
            <button onClick={load} className="btn-brut">
              <Search size={16} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-black/60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="brut-card p-8 text-center" data-testid={ADMIN.emptyState}>
          <div className="font-mono text-xs uppercase tracking-widest text-black/60 mb-1">
            No candidates match
          </div>
          <div>Try clearing filters or check back once applications arrive.</div>
        </div>
      ) : (
        <div className="brut-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[rgb(var(--ink))] text-[rgb(var(--paper))]">
              <tr>
                <Th>Reference</Th>
                <Th>Name</Th>
                <Th>Client</Th>
                <Th>Project</Th>
                <Th className="hidden lg:table-cell">Role</Th>
                <Th className="hidden md:table-cell">Immigration</Th>
                <Th>Readiness</Th>
                <Th>Status</Th>
                <Th>Docs</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  data-testid={ADMIN.candidateRow(c.id)}
                  className="border-t-2 border-black hover:bg-[rgb(var(--paper-2))]"
                >
                  <Td><span className="font-mono">{c.reference}</span></Td>
                  <Td className="font-medium">{c.full_name}</Td>
                  <Td>
                    <span className="pill pill-ink text-[10px]">
                      {c.client_name || "—"}
                    </span>
                  </Td>
                  <Td>
                    <span className="pill pill-flame text-[10px]">
                      {c.project_label || c.project || "—"}
                    </span>
                  </Td>
                  <Td className="hidden lg:table-cell text-xs">
                    {c.role_label || "—"}
                  </Td>
                  <Td className="hidden md:table-cell">
                    <span className="pill pill-muted text-[10px]">
                      {c.immigration_status?.replace("_", " ")}
                    </span>
                  </Td>
                  <Td>
                    <ReadinessPill status={c.readiness_status} />
                  </Td>
                  <Td><StatusPill status={c.status} /></Td>
                  <Td>
                    <span className="font-mono text-xs whitespace-nowrap">
                      {c.doc_count}
                      {c.docs_pending > 0 && (
                        <span className="ml-2 pill pill-flame text-[10px]">
                          {c.docs_pending} pending
                        </span>
                      )}
                    </span>
                  </Td>
                  <Td>
                    <Link
                      to={`/admin/candidates/${c.id}`}
                      data-testid={ADMIN.candidateOpen(c.id)}
                      className="btn-brut btn-ghost text-xs"
                    >
                      Open <ArrowRight size={12} />
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.14em] ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}

function ReadinessPill({ status }) {
  const s = status || "not_ready";
  const map = {
    not_ready: { cls: "pill-muted", label: "Not ready" },
    awaiting_verification: { cls: "pill-flame", label: "Awaiting admin" },
    ready: { cls: "pill-success", label: "Ready" },
    vehicle_rejected: { cls: "pill-danger", label: "Rejected" },
  };
  const m = map[s] || map.not_ready;
  return <span className={`pill ${m.cls} text-[10px]`}>{m.label}</span>;
}
